import React, { useState, useRef, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { Briefcase, Plus, Clock, CheckCircle, AlertCircle, DollarSign, User, Calendar, ArrowLeft, ChevronRight, MapPin, Users, Edit3 } from 'lucide-react';
import { format, addDays } from 'date-fns';

export default function JobsBoard({ kidId, isParent = false }) {
  const { 
    allowanceJobs, 
    family,
    createJob,
    acceptJob,
    completeJob,
    approveJobCompletion,
    rejectJobCompletion
  } = useFamilyContext();
  
  const titleInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const [titleFocused, setTitleFocused] = useState(false);
  const [descriptionFocused, setDescriptionFocused] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [schedulingMode, setSchedulingMode] = useState('today'); // 'today', 'tomorrow', 'date'

  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    amount: '',
    completion_deadline: format(new Date(), 'yyyy-MM-dd'),
    completion_time: '17:00'
  });

  // Auto-focus title input when form opens
  useEffect(() => {
    if (showCreateForm) {
      titleInputRef.current?.focus();
    }
  }, [showCreateForm]);

  // Handle Enter key navigation
  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      descriptionInputRef.current?.focus();
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Scheduling handlers
  const handleToday = () => {
    setNewJob(prev => ({
      ...prev,
      completion_deadline: format(new Date(), 'yyyy-MM-dd'),
      completion_time: '17:00'
    }));
    setSchedulingMode('today');
    setShowCalendar(false);
  };
  const handleTomorrow = () => {
    setNewJob(prev => ({
      ...prev,
      completion_deadline: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      completion_time: '17:00'
    }));
    setSchedulingMode('tomorrow');
    setShowCalendar(false);
  };
  const handlePickDate = () => {
    setShowCalendar(true);
    setSchedulingMode('date');
  };
  const navigateCalendar = (direction) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarDate(newDate);
  };
  const selectCalendarDate = (day) => {
    const selectedDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
    setNewJob(prev => ({ ...prev, completion_deadline: format(selectedDate, 'yyyy-MM-dd') }));
    setShowCalendar(false);
    setSchedulingMode('date');
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      if (!newJob.title || !newJob.description || !newJob.amount || !newJob.completion_deadline) {
        alert('Please fill in all required fields');
        return;
      }

      // Combine date and time
      const deadline = new Date(`${newJob.completion_deadline}T${newJob.completion_time}`);
      
      await createJob({
        ...newJob,
        completion_deadline: deadline.toISOString()
      });
      
      setNewJob({ 
        title: '', 
        description: '', 
        amount: '', 
        completion_deadline: format(new Date(), 'yyyy-MM-dd'),
        completion_time: '17:00'
      });
      setShowCreateForm(false);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleJobAction = async (job, action) => {
    try {
      let reason;
      switch (action) {
        case 'accept':
          await acceptJob(job.id, kidId);
          break;
        case 'complete':
          await completeJob(job.id, kidId);
          break;
        case 'approve':
          await approveJobCompletion(job.id);
          break;
        case 'reject':
          reason = prompt('Why are you rejecting this? (optional)');
          await rejectJobCompletion(job.id, reason || '');
          break;
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const getJobsForView = () => {
    const now = new Date();
    if (isParent) {
      return allowanceJobs; // Parents see all jobs
    } else {
      // Kids see available jobs and their own jobs
      return allowanceJobs.filter(job => {
        // If job is past deadline and not accepted by this kid, don't show it
        if (job.completion_deadline && new Date(job.completion_deadline) < now && job.accepted_by !== kidId) {
          return false;
        }
        return job.status === 'available' || job.accepted_by === kidId;
      });
    }
  };

  const getJobStatusColor = (status) => {
    if (status === 'pending') return 'bg-stone-100 text-stone-800';
    if (status === 'in_progress') return 'bg-teal-100 text-teal-500';
    if (status === 'completed') return 'bg-teal-500 text-teal-800';
    return 'bg-stone-100 text-stone-800';
  };

  const getJobStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <Briefcase className="w-4 h-4" />;
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      case 'completed':
        return <AlertCircle className="w-4 h-4" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Briefcase className="w-4 h-4" />;
    }
  };

  const getKidName = (kidId) => {
    const kid = family?.kids?.find(k => k.id === kidId);
    return kid?.name || 'Unknown';
  };

  const jobs = getJobsForView();

  return (
    <div className="space-y-4">
      {/* Add Job Button - only show if parent and no jobs exist */}
      {isParent && jobs.length === 0 && !showCreateForm && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Job
          </button>
        </div>
      )}

      {/* Create Job Form */}
      {showCreateForm && isParent && (
        <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center">
              <Plus className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Create New Job</h3>
          </div>

          <form onSubmit={handleCreateJob} className="space-y-4">
            {/* Job Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Job Title</label>
              <input
                ref={titleInputRef}
                type="text"
                value={newJob.title}
                onChange={(e) => setNewJob(prev => ({ ...prev, title: e.target.value }))}
                onKeyPress={handleTitleKeyPress}
                onFocus={() => setTitleFocused(true)}
                onBlur={() => setTitleFocused(false)}
                className={`w-full px-3 py-2 border rounded-lg transition-all ${
                  titleFocused 
                    ? 'border-teal-500 bg-teal-50/50' 
                    : 'border-stone-300 hover:border-stone-400'
                } focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500`}
                placeholder="e.g., Clean the garage"
                required
              />
            </div>

            {/* Job Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
              <textarea
                ref={descriptionInputRef}
                value={newJob.description}
                onChange={(e) => setNewJob(prev => ({ ...prev, description: e.target.value }))}
                onFocus={() => setDescriptionFocused(true)}
                onBlur={() => setDescriptionFocused(false)}
                className={`w-full px-3 py-2 border rounded-lg transition-all ${
                  descriptionFocused 
                    ? 'border-teal-500 bg-teal-50/50' 
                    : 'border-stone-300 hover:border-stone-400'
                } focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500`}
                placeholder="What needs to be done?"
                rows="3"
                required
              />
            </div>

            {/* Payment */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Payment</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-stone-500">$</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newJob.amount}
                  onChange={(e) => setNewJob(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full pl-8 pr-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            {/* Scheduling */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Due Date</label>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <button
                  type="button"
                  onClick={handleToday}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    schedulingMode === 'today'
                      ? 'bg-teal-600 text-white shadow-md'
                      : 'bg-teal-50 text-teal-700 hover:bg-teal-200'
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={handleTomorrow}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    schedulingMode === 'tomorrow'
                      ? 'bg-teal-600 text-white shadow-md'
                      : 'bg-teal-50 text-teal-700 hover:bg-teal-200'
                  }`}
                >
                  Tomorrow
                </button>
                <button
                  type="button"
                  onClick={handlePickDate}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    schedulingMode === 'date'
                      ? 'bg-teal-600 text-white shadow-md'
                      : 'bg-teal-50 text-teal-700 hover:bg-teal-200'
                  }`}
                >
                  Pick Date
                </button>
              </div>

              {/* Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-stone-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={newJob.completion_deadline}
                    onChange={(e) => setNewJob(prev => ({ ...prev, completion_deadline: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-stone-600 mb-1">Time</label>
                  <input
                    type="time"
                    value={newJob.completion_time}
                    onChange={(e) => setNewJob(prev => ({ ...prev, completion_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
            </div>

            {/* Calendar (if showing) */}
            {showCalendar && (
              <div className="border border-stone-200 rounded-lg p-4 bg-stone-50">
                <div className="flex items-center justify-between mb-3">
                  <button type="button" onClick={() => navigateCalendar(-1)}>
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h4 className="font-medium">{format(calendarDate, 'MMMM yyyy')}</h4>
                  <button type="button" onClick={() => navigateCalendar(1)}>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                {/* Calendar grid would go here - simplified for now */}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
              >
                Create Job
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Jobs List */}
      {jobs.length > 0 && (
        <div className="space-y-3">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {getJobStatusIcon(job.status)}
                    <h4 className="font-semibold text-slate-900">{job.title}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getJobStatusColor(job.status)}`}>
                      {job.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-stone-600 mb-2">{job.description}</p>
                  
                  <div className="flex items-center gap-4 text-xs text-stone-500">
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      <span className="font-bold text-teal-600 text-lg">{formatMoney(job.amount)}</span>
                    </div>
                    {job.completion_deadline && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Due {format(new Date(job.completion_deadline), 'MMM d, h:mm a')}</span>
                      </div>
                    )}
                    {job.accepted_by && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span>Assigned to {getKidName(job.accepted_by)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Job Actions */}
              <div className="flex gap-2">
                {job.status === 'available' && !isParent && (
                  <button
                    onClick={() => handleJobAction(job, 'accept')}
                    className="flex-1 bg-teal-600 text-white py-2 px-3 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                  >
                    Accept Job
                  </button>
                )}
                
                {job.status === 'in_progress' && job.accepted_by === kidId && (
                  <button
                    onClick={() => handleJobAction(job, 'complete')}
                    className="flex-1 bg-teal-600 text-white py-2 px-3 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                  >
                    Mark Complete
                  </button>
                )}
                
                {job.status === 'completed' && isParent && (
                  <>
                    <button
                      onClick={() => handleJobAction(job, 'approve')}
                      className="flex-1 bg-teal-600 text-white py-2 px-3 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                    >
                      Approve & Pay
                    </button>
                    <button
                      onClick={() => handleJobAction(job, 'reject')}
                      className="px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 transition-colors text-sm"
                    >
                      Reject
                    </button>
                  </>
                )}
                
                {job.status === 'approved' && (
                  <div className="flex-1 text-center py-2 px-3 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">
                    ✓ Completed & Paid
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {jobs.length === 0 && !showCreateForm && (
        <div className="text-center py-8">
          <div className="text-3xl mb-3 opacity-50">🎯</div>
          <h4 className="text-base font-semibold text-slate-900 mb-1">
            {isParent ? 'No jobs created yet' : 'No jobs available'}
          </h4>
          <p className="text-stone-500 text-sm mb-4">
            {isParent 
              ? 'Create jobs for kids to earn extra allowance' 
              : 'Check back later for new earning opportunities'
            }
          </p>
          {isParent && (
            <button 
              onClick={() => setShowCreateForm(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Create First Job
            </button>
          )}
        </div>
      )}
    </div>
  );
}